'use client';

import { Transition } from '@headlessui/react';
import { Trans, useTranslation } from 'next-i18next';
import { useBjForm } from '@hooks/useBjForm';
import { useCreditsCalculator } from '@hooks/credits/useCreditsCalculator';
import {
  Input, Button, FormError,
} from '@components/common/Forms';
import { TailwindSpinner } from '@components/common';
import React, {
  type ChangeEvent, useEffect, useMemo, useState,
} from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/16/solid';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useSwrAccountDiscountData } from '@hooks/useSwrApi/endpoints/order/useSwrAccountDiscountData';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';

/**
 * @description Interface for the discount calculator.
 */
interface DiscountCalculatorProps {
  triggerCartDetails: <Data, Arg>(data: Arg) => Promise<Data | void>,
  loadingNextStep: boolean,
  prevCreditsAmount?: number,
  targetAmount?: number,
  transitionProps?: object,
  closeModal?: () => void,
}

/**
 * @description Renders the discount calculator.
 * - `triggerCartDetails` - Function to call when the user clicks the submit button.
 * - `loadingNextStep` - Boolean to show loading state on the submit button.
 * - `prevCreditsAmount` - The previously set credits amount if available. Used to set the initial value when user goes back.
 * - `transitionProps` - Common transition props for the Transition component. If not provided, there will be no transition.
 * - `closeModal` - Optional function to close the modal.
 */
export const DiscountCalculator = (props: DiscountCalculatorProps) => {
  // Destructure props
  const {
    triggerCartDetails,
    loadingNextStep,
    prevCreditsAmount,
    targetAmount,
    transitionProps,
  } = props;

  // Locale and account discount data (to get nextDiscountAmount)
  const { locale } = useExtendedRouter();
  const { data: accountDiscountData } = useSwrAccountDiscountData({ locale, fetchCondition: true });

  // Input constraints mirror useCreditsCalculator defaults for initial clamping
  const INPUT_MIN = 1;
  const INPUT_MAX = 200000;

  // Initial credits amount
  const fallbackInitial = useMemo(() => {
    const next = accountDiscountData?.amount;
    return typeof next === 'number' ? Math.round(next) : undefined;
  }, [accountDiscountData?.amount]);

  // If a targetAmount is provided and we know last year's spend, set default so lastYear + input = target
  const computedFromTarget = useMemo(() => {
    if (typeof targetAmount !== 'number') return undefined;
    type WithLastYear = { lastYearOrderValueEur?: number };

    const lastYearMaybe = (accountDiscountData as unknown as WithLastYear)?.lastYearOrderValueEur;
    if (typeof lastYearMaybe !== 'number') return undefined;

    const lastYear = Math.round(lastYearMaybe);
    const desiredRaw = Math.max(0, Math.round(targetAmount - lastYear));
    return Math.max(INPUT_MIN, Math.min(INPUT_MAX, desiredRaw));
  }, [targetAmount, accountDiscountData]);

  const initialCreditsAmount = targetAmount ?? computedFromTarget ?? prevCreditsAmount ?? fallbackInitial;
  const [creditAmountValue, setCreditAmountValue] = useState(initialCreditsAmount ?? 0);
  const [hasCreditAmountBeenAdjusted, setHasCreditAmountBeenAdjusted] = useState(false);
  const [giftPercentageValue, setGiftPercentageValue] = useState(0);
  const [hasGiftPercentageBeenAdjusted, setHasGiftPercentageBeenAdjusted] = useState(false);
  const [giftPercentageIsValid, setGiftPercentageIsValid] = useState(true);
  const headerInfo = useAppSelector(getUserHeaderInfo);
  const isAdminUser = headerInfo?.adminUser === true;

  // Translations
  const { t } = useTranslation('common');

  // Credit calculator hook
  const {
    minValue,
    maxValue,
    isValid,
    creditsAmount,
    priceData,
    priceIsValidating,
  } = useCreditsCalculator(
    creditAmountValue,
  );

  const {
    record,
    handleCallbackSubmit,
  } = useBjForm();
  const [showDiscount, setShowDiscount] = useState(false);
  const discountLevels = priceData?.discountLevels ?? [];
  const nextTier = discountLevels
    .filter((level) => (level.minAmount ?? 0) > creditsAmount)
    .sort((a, b) => (a.minAmount ?? 0) - (b.minAmount ?? 0))[0];
  const giftPercentageIsReady = !isAdminUser || hasGiftPercentageBeenAdjusted || !priceIsValidating;

  const differenceToNextTier = nextTier ? Math.round((nextTier.minAmount ?? 0) - creditsAmount) : 0;
  const nextTierPercentage = nextTier?.percentage;
  const showNextTierSuggestion = nextTier && differenceToNextTier > 0;

  useEffect(() => {
    if (hasCreditAmountBeenAdjusted) return;
    if (typeof initialCreditsAmount !== 'number') return;
    setCreditAmountValue(initialCreditsAmount);
  }, [hasCreditAmountBeenAdjusted, initialCreditsAmount]);

  useEffect(() => {
    if (!isAdminUser) return;
    if (hasGiftPercentageBeenAdjusted) return;
    if (typeof priceData?.bonusPercentage !== 'number') return;
    setGiftPercentageValue(priceData.bonusPercentage);
  }, [hasGiftPercentageBeenAdjusted, isAdminUser, priceData?.bonusPercentage]);

  const handleCreditAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    setHasCreditAmountBeenAdjusted(true);
    const { value } = event.target;
    setCreditAmountValue(value === '' ? 0 : Number(value));
  };

  const handleGiftPercentageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setHasGiftPercentageBeenAdjusted(true);
    setGiftPercentageIsValid(event.target.validity.valid);
    const { value } = event.target;
    setGiftPercentageValue(value === '' ? 0 : Number(value));
  };


  // Render component
  // ********************************
  return (
    <div className="min-w-full md:min-w-[600px]">
      {/* Neuro calculator */}
      <form
        noValidate
        onSubmit={(event) => handleCallbackSubmit(event, triggerCartDetails)}
      >
        {/* Form content */}
        <Transition
          as="div"
          className="max-w-full px-6 py-4"
          {...transitionProps}
        >

          {/* Input amount */}
          <label htmlFor="creditAmount" className="w-full">
            <span className="ml-2 font-bold text-ink-medium">
              {' '}
              {t('wallet.top-up.credit_amount_label')}
            </span>
            {/* input element */}
            <Input
              className="asOptional mt-0.5"
              type="number"
              min={minValue}
              max={maxValue}
              value={creditAmountValue || ''}
              onChange={handleCreditAmountChange}
              {...record('creditAmount', { convertEmptyValue: 'toNumber' })}
            />

            {/* input error element */}
            <FormError
              errorMessage={t('error.message.not_in_range', { min: minValue, max: maxValue })}
              isShowing={!isValid}
            />
          </label>

          {/* Show prices */}
          <span className="ml-2 text-sm text-ink-medium">
            {t('euro_ron_fixed_exchange_rate')}
          </span>

          { // If loading, show spinner, else show amount or 0
            <div className="mt-2 rounded-md bg-surface-100 p-2">
              <div className="grid grid-cols-2 gap-2 py-1 text-sm font-thin">
                <div>{t('page.offer.calculator.total.credits')}</div>
                <div className="text-right">
                  {priceIsValidating ? <TailwindSpinner className="size-4 text-ink-medium" /> : priceData?.amount ?? 0}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 py-1 text-sm font-thin">
                <div>{t('price.calculator.top-up.bonus_percentage')}</div>
                <div className="text-right">
                  {isAdminUser ? (
                    <div className="ml-auto flex items-center justify-end gap-1">
                      {priceIsValidating && <TailwindSpinner className="size-4 text-ink-medium" />}
                      <Input
                        className="asOptional h-7 w-20 py-1 text-right"
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={giftPercentageValue}
                        onChange={handleGiftPercentageChange}
                        withError={false}
                        {...record('giftPercentage', { convertValue: 'toNumber' })}
                      />
                      %
                    </div>
                  ) : (
                    <>
                      {priceIsValidating ? <TailwindSpinner className="size-4 text-ink-medium" /> : priceData?.bonusPercentage ?? 0}
                      %
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 py-1 text-sm font-thin">
                <div>{t('price.calculator.top-up.bonus_amount')}</div>
                <div className="text-right">
                  {priceIsValidating ? <TailwindSpinner className="size-4 text-ink-medium" /> : priceData?.bonusAmount ?? 0}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 py-1 text-sm font-thin">
                <div>{t('price.calculator.top-up.next_bonus_level')}</div>
                <div className="text-right">
                  {priceIsValidating
                    ? <TailwindSpinner className="size-4 text-ink-medium" />
                    : (
                      <>
                        {priceData?.nextBonusPercentage ?? 0}
                        {'% '}
                        (
                        {priceData?.nextBonusAmount ?? 0}
                        {' €'}
                        )
                      </>
                    )}
                </div>
              </div>
            </div>
          }

          {showNextTierSuggestion && (
            <button
              type="button"
              onClick={() => {
                setHasCreditAmountBeenAdjusted(true);
                setCreditAmountValue(creditsAmount + differenceToNextTier);
              }}
              className="mx-auto mt-2 flex w-fit items-center gap-2 rounded-md bg-secondary/10 px-2 py-1 text-xs font-medium text-secondary hover:bg-secondary/20"
            >
              <Trans
                i18nKey="page.offer-configurator.bonus_supplement.desc"
                values={{
                  nrOfCredits: differenceToNextTier,
                  nrPercentageBonus: nextTierPercentage,
                }}
              />
            </button>
          )}

        </Transition>

        {/* Submit button */}
        <div className="border-t px-6 py-4 text-right text-sm">
          <Button
            type="submit"
            rounding="full"
            color="secondary"
            size="sm"
            className="w-full !px-7"
            disabled={!(creditsAmount >= minValue && creditsAmount <= maxValue) || (isAdminUser && (!giftPercentageIsValid || !giftPercentageIsReady))}
            isLoading={loadingNextStep}
          >
            {t('order.choosePayment.title')}
          </Button>
        </div>

        <div
          role="presentation"
          onClick={() => setShowDiscount((prev) => !prev)}
          className="flex cursor-pointer items-center justify-center gap-2 pb-5 pt-2 text-center text-sm text-primary underline"
        >
          {t('see.price.calculator.top-up.discount.levels')}
          {showDiscount ? <ChevronUpIcon className="size-5" /> : <ChevronDownIcon className="size-5" />}
        </div>

        {showDiscount && priceData?.discountLevels && priceData?.discountLevels.map((level) => (
          <div key={`${level.minAmount}-amount`} className="mb-2 grid grid-cols-3 border-b px-6 py-1">
            <div className="col-span-2">
              ≥
              {level.minAmount}
            </div>
            <div className="text-right">
              {level.percentage}
              %
            </div>
          </div>
        ))}

      </form>
    </div>
  );
};
